import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { useApiClient } from '@marageti/z4-lib';
import { useSnackbar } from 'notistack';
import useTravelerProfile from '../../../hooks/use-traveler-profile';
import useSuggestionsReducer from './use-suggestions-reducer';
import {
  apply as applyAction,
  applyAll as applyAllAction,
  cancelDismissAll as cancelDismissAllAction,
  confirmDismissAll as confirmDismissAllAction,
  dismiss as dismissAction,
  dismissNoResultsAlert as dismissNoResultsAlertAction,
  requestDismissAll as requestDismissAllAction,
  resetForTraveler,
  resetReview as resetReviewAction,
  skipEntryForSession as skipEntryForSessionAction,
  startReviewBegin,
  startReviewError,
  startReviewNoResults,
  startReviewSuccess,
  unapply as unapplyAction,
  undismiss as undismissAction,
} from './actions';
import {
  acceptAllPending,
  buildCommitPartial,
  buildSuggestionEntries,
  isSuggestionsStateEmpty,
  profileIsGatedEmpty,
  unwrapPipeline,
} from './utils';
import {
  FabMode,
  SuggestionLocator,
  SuggestionsState,
  ValueProps,
} from './types';

export const TravelerProfileSuggestionsStore = createContext<ValueProps | null>(null);

type ProviderProps = {
  travelerId: string | undefined;
  canEdit: boolean;
  children: React.ReactNode;
};

export const TravelerProfileSuggestionsProvider = ({
  travelerId,
  canEdit,
  children,
}: ProviderProps) => {
  const apiClient = useApiClient();
  const { enqueueSnackbar } = useSnackbar();
  const [state, dispatch] = useSuggestionsReducer();

  // `isSaving` here only reflects saves initiated through this provider
  // (the suggestions FAB). Other consumers of `useTravelerProfile` (e.g.
  // the rich-text sections) maintain their own `isSaving` flag. Today no
  // non-suggestion save can run while the AI review is open, but be aware
  // if that ever changes — the FAB / unsaved-changes blocker reads this
  // copy.
  const { travelerProfile, updateTravelerProfile, isSaving } = useTravelerProfile(travelerId);

  // Defensive reset when the active traveler changes. The provider is
  // already keyed on `travelerId` upstream so this is normally a no-op,
  // but it keeps the provider safe to mount in any consumer.
  useEffect(() => {
    dispatch(resetForTraveler());
  }, [travelerId]);

  const canEnter = !!(
    canEdit
    && travelerId
    && profileIsGatedEmpty(travelerProfile)
    && !state.skippedForVisit
  );

  const hasPending = useMemo(() => (
    state.suggestions.significantDates?.status === 'pending'
    || Object.values(state.suggestions.text).some((e) => e?.status === 'pending')
    || Object.values(state.suggestions.travelPreferences).some((e) => e?.status === 'pending')
  ), [state.suggestions]);

  const hasAcceptedUncommitted = useMemo(() => (
    state.suggestions.significantDates?.status === 'accepted'
    || Object.values(state.suggestions.text).some((e) => e?.status === 'accepted')
    || Object.values(state.suggestions.travelPreferences).some((e) => e?.status === 'accepted')
  ), [state.suggestions]);

  const fabMode: FabMode = useMemo(() => {
    if (!state.isReviewing) return 'hidden';
    if (hasPending) return 'apply-all';
    if (hasAcceptedUncommitted) return 'save';
    return 'hidden';
  }, [state.isReviewing, hasPending, hasAcceptedUncommitted]);

  const startReview = useCallback(async () => {
    if (!travelerId) return;
    dispatch(startReviewBegin());
    try {
      const response = await apiClient.peopleClient.getTravelerProfileSuggestions(travelerId);
      const pipeline = unwrapPipeline(response);
      const next = buildSuggestionEntries(pipeline?.step2ProfileDocument ?? {});
      if (isSuggestionsStateEmpty(next)) {
        dispatch(startReviewNoResults());
        return;
      }
      dispatch(startReviewSuccess(next, pipeline?.step3Validation));
    } catch (err) {
      const message = err instanceof Error && err.message
        ? err.message
        : 'Could not load AI profile suggestions.';
      enqueueSnackbar(message, { variant: 'default' });
      dispatch(startReviewError());
    }
  }, [travelerId, apiClient, enqueueSnackbar, dispatch]);

  const commit = useCallback(
    async (source: SuggestionsState) => {
      const partial = buildCommitPartial(source, travelerProfile);
      if (Object.keys(partial).length === 0) {
        dispatch(resetReviewAction());
        return;
      }
      const result = await updateTravelerProfile(
        partial,
        'Could not update profile.',
        'Profile updated',
      );
      if (result) {
        dispatch(resetReviewAction());
      }
    },
    [travelerProfile, updateTravelerProfile, dispatch],
  );

  const applyAll = useCallback(() => {
    // Promote every pending suggestion to `accepted` but do NOT commit. The
    // user still needs to hit "Save changes" (the FAB flips to its save
    // mode automatically once nothing is pending) before any network write
    // happens. This lets reviewers eyeball the staged values one more time
    // and gives them a chance to undo individual fields before committing.
    const next = acceptAllPending(state.suggestions);
    dispatch(applyAllAction(next));
  }, [state.suggestions, dispatch]);

  const save = useCallback(async () => {
    await commit(state.suggestions);
  }, [commit, state.suggestions]);

  // All single-action dispatchers are wrapped in useCallback so their
  // identity is stable across renders. Consumers (notably the per-field
  // preview hooks) memoize on these refs to keep their derived
  // `SuggestionPreview` objects stable when nothing relevant changed.
  const apply = useCallback((locator: SuggestionLocator) => dispatch(applyAction(locator)), [dispatch]);
  const unapply = useCallback((locator: SuggestionLocator) => dispatch(unapplyAction(locator)), [dispatch]);
  const dismiss = useCallback((locator: SuggestionLocator) => dispatch(dismissAction(locator)), [dispatch]);
  const undismiss = useCallback((locator: SuggestionLocator) => dispatch(undismissAction(locator)), [dispatch]);
  const skipEntryForSession = useCallback(() => dispatch(skipEntryForSessionAction()), [dispatch]);
  const requestDismissAll = useCallback(() => dispatch(requestDismissAllAction()), [dispatch]);
  const cancelDismissAll = useCallback(() => dispatch(cancelDismissAllAction()), [dispatch]);
  const confirmDismissAll = useCallback(() => dispatch(confirmDismissAllAction()), [dispatch]);
  const resetReview = useCallback(() => dispatch(resetReviewAction()), [dispatch]);
  const dismissNoResultsAlert = useCallback(() => dispatch(dismissNoResultsAlertAction()), [dispatch]);

  const value: ValueProps = useMemo(
    () => ({
      ...state,
      canEnter,
      isSaving,
      hasAnyUnresolvedOrUncommitted: hasPending || hasAcceptedUncommitted,
      fabMode,
      startReview,
      skipEntryForSession,
      apply,
      unapply,
      dismiss,
      undismiss,
      applyAll,
      requestDismissAll,
      cancelDismissAll,
      confirmDismissAll,
      save,
      resetReview,
      dismissNoResultsAlert,
    }),
    [
      state,
      canEnter,
      isSaving,
      hasPending,
      hasAcceptedUncommitted,
      fabMode,
      startReview,
      skipEntryForSession,
      apply,
      unapply,
      dismiss,
      undismiss,
      applyAll,
      requestDismissAll,
      cancelDismissAll,
      confirmDismissAll,
      save,
      resetReview,
      dismissNoResultsAlert,
    ],
  );

  return (
    <TravelerProfileSuggestionsStore.Provider value={value}>
      {children}
    </TravelerProfileSuggestionsStore.Provider>
  );
};
